import Link from 'next/link'
import Image from 'next/image'
import { css, Theme } from '@emotion/react'
import { Box, Typography, Breadcrumbs } from '@mui/material'
import { parseISO, format } from 'date-fns'
import { categories } from '~/constants/categories'

type Props = {
  title: string
  date: string
  publishedAt?: string
  updatedAt?: string
  width: number
  height: number
  url: string
  filename: string
  category: string[]
}

const BlogHead: React.FC<Props> = ({
  title,
  date,
  publishedAt,
  updatedAt,
  width,
  height,
  url,
  filename,
  category,
}) => {
  const publishDates = parseISO(
    publishedAt ?? '2021-05-17T00:56:58.487363+00:00'
  )
  const updateDates = parseISO(updatedAt)

  return (
    <>
      <Box mx={5} css={styles.header}>
        <Breadcrumbs maxItems={3} aria-label='breadcrumb'>
          <Link href='/' passHref>
            <a css={styles.breadHome}>Home</a>
          </Link>
          <Box>
            {category.map(item => {
              const name: string = categories[`${item}`].name
              return (
                <Link href={`/blog/category/${item}`} passHref key={item}>
                  <a css={styles.category}>{name}</a>
                </Link>
              )
            })}
          </Box>
        </Breadcrumbs>
        <Typography
          variant='h1'
          component='h1'
          css={styles.title}
          color='primary'>
          {title}
        </Typography>
        <Typography variant='h4' component='h4' color='primary'>
          <time dateTime={updatedAt} css={styles.publishDate}>
            {format(updateDates, 'yyyy年M月d日')}
          </time>
          <time dateTime={publishedAt} css={styles.updateDate}>
            {format(publishDates, 'yyyy年M月d日')}
          </time>
        </Typography>
      </Box>
      <Box css={styles.image}>
        <Image
          src={url}
          alt={filename}
          layout='responsive'
          height={height}
          width={width}
        />
      </Box>
    </>
  )
}

const styles = {
  header: (theme: Theme) => css`
    display: flex;
    flex-flow: column;
    width: 100%;
    margin: 15vh 0 0;
    @media (max-width: 1280px) {
      width: auto;
      margin: 12vh 15vw 0;
    }
    @media (max-width: 768px) {
      margin: 80px 4vw 0;
    }
  `,
  breadHome: (theme: Theme) => css`
    color: ${theme.palette.primary.main};
    font-family: 'Noto sans JP', sans-serif;
    font-weight: 500;
    font-size: 1rem;
    &:before {
      content: '\\f015';
      margin-right: 5px;
      font-family: 'Font Awesome 5 Free';
      font-weight: 900;
      font-size: 1rem;
    }
    @media (max-width: 768px) {
      font-size: 0.9rem;
      &:before {
        font-size: 0.9rem;
      }
    }
  `,
  category: (theme: Theme) => css`
    color: ${theme.palette.primary.main};
    font-family: 'Noto sans JP', sans-serif;
    font-weight: 500;
    font-size: 1rem;
    padding-right: 10px;
    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
  `,
  title: css`
    font-family: 'Noto Sans JP', sans-serif;
    font-size: 3rem;
    font-weight: 700;
    margin: 5vh 0;
    @media (max-width: 768px) {
      font-size: 1.5rem;
      margin: 30px 0;
    }
  `,
  publishDate: css`
    font-family: 'Noto sans JP', sans-serif;
    font-size: 0.8rem;
    font-weight: 500;
    margin-right: 15px;
    &:before {
      content: '\\f017';
      margin-right: 5px;
      font-family: 'Font Awesome 5 Free';
      font-weight: 900;
      font-size: 0.8rem;
    }
  `,
  updateDate: css`
    font-family: 'Noto sans JP', sans-serif;
    font-size: 0.8rem;
    font-weight: 500;
    margin-right: 15px;
    &:before {
      content: '\\f2f1';
      margin-right: 5px;
      font-family: 'Font Awesome 5 Free';
      font-weight: 900;
      font-size: 0.8rem;
    }
  `,
  image: css`
    margin: 15px 0 0;
    box-shadow: 0 5px 10px rgba(0, 0, 0, 20%);
    @media (max-width: 1280px) {
      width: auto;
      margin: 15px 15vw 0;
    }
    @media (max-width: 768px) {
      margin: 15px 0 30px;
    }
  `,
}

export default BlogHead
